import { useQuery } from '@tanstack/react-query';
import { getPluginUsers } from '@kirin/api-client/api/plugins';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

/**
 * Identity for a plugin row in the list. `pluginName` alone is not unique
 * within an org (two unrelated public plugins from different marketplaces
 * can share a name), so the drill-down keys off the (pluginName, marketplace)
 * pair. `null` marketplace is its own bucket.
 *
 * Pass `null` for `identity` while the selection is empty — the query stays
 * disabled.
 */
export interface PluginIdentity {
  pluginName: string;
  marketplace: string | null;
}

export function usePluginUsers(
  organizationId: string | undefined,
  identity: PluginIdentity | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.plugins.users(organizationId, identity),
    queryFn: () => {
      if (!organizationId || !identity) {
        throw new Error('organizationId and identity are required');
      }
      return getPluginUsers(
        apiClient,
        organizationId,
        identity.pluginName,
        identity.marketplace
      );
    },
    enabled: enabled && !!organizationId && !!identity,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}
